import { Button } from '@ui5/webcomponents-react';
import { ResultUserMessage, UserMessage } from 'components/shared/UserMessage';
import {
  ForwardReview,
  Status,
  useForwardToAnswererMutation,
  useForwardToCreatorMutation,
  User
} from 'generated/graphql';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStyles } from './ForwardQuery.jss';

import ButtonDesign from '@ui5/webcomponents/dist/types/ButtonDesign.js';

type UserProps = Pick<User, 'internal'> | null;

export type ForwardQueryProps = {
  id: string;
  status?: Status;
  forwardReviewTo?: ForwardReview | null;
  user?: UserProps;
};

export const ForwardQuery: React.FC<ForwardQueryProps> = (props: ForwardQueryProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { id, user, forwardReviewTo, status } = props;

  const [, forwardToAnswererMutation] = useForwardToAnswererMutation();
  const [, forwardToCreatorMutation] = useForwardToCreatorMutation();

  const [isUserMessageOpen, setIsUserMessageOpen] = useState(false);
  const [resultMessage, setResultMessage] = useState<ResultUserMessage>({
    isSuccess: false,
    message: ''
  });

  const setUserMessage = (message: ResultUserMessage) => {
    setResultMessage(message);
    setIsUserMessageOpen(true);
  };

  const forwardToAnswerer = async () => {
    const result = await forwardToAnswererMutation({ id });

    if (result.error) {
      setUserMessage({
        isSuccess: false,
        message: t('forward-query-answerer-error-message')
      });
    } else {
      setUserMessage({
        isSuccess: true,
        message: t('forward-query-answerer-success-message')
      });
    }
  };

  const forwardToCreator = async () => {
    const result = await forwardToCreatorMutation({ id });

    if (result.error) {
      setUserMessage({
        isSuccess: false,
        message: t('forward-query-creator-error-message')
      });
    } else {
      setUserMessage({
        isSuccess: true,
        message: t('forward-query-creator-success-message')
      });
    }
  };

  const getForwardToAnswererButton = () => {
    if (forwardReviewTo === ForwardReview.ANSWERER) {
      return (
        <Button
          className={classes.button}
          data-testid="display-query-forward-to-answerer"
          data-help-id="display-query-forward-to-answerer"
          id="forward-to-answerer-button"
          onClick={forwardToAnswerer}
          design={ButtonDesign.Default}
        >
          {t('forward-query-answerer-button')}
        </Button>
      );
    }
    return null;
  };

  const getForwardToCreatorButton = () => {
    if (forwardReviewTo === ForwardReview.CREATOR) {
      return (
        <Button
          className={classes.button}
          data-testid="display-query-forward-to-creator"
          data-help-id="display-query-forward-to-creator"
          id="forward-to-creator-button"
          onClick={forwardToCreator}
          design={ButtonDesign.Default}
        >
          {t('forward-query-creator-button')}
        </Button>
      );
    }
    return null;
  };

  return status === Status.PENDING_REVIEW && user?.internal ? (
    <>
      {getForwardToAnswererButton()}
      {getForwardToCreatorButton()}
      <UserMessage
        isVisible={isUserMessageOpen}
        resultMessage={resultMessage}
        onResetUserMessage={() => setIsUserMessageOpen(false)}
      />
    </>
  ) : null;
};
